<template>
  <b-modal
    :id="id"
    header-bg-variant="primary"
    header-text-variant="light"
    :title="modalTitle"
    :ok-disabled="loading || saving"
    :cancel-disabled="saving"
    :no-close-on-backdrop="saving"
    :no-close-on-esc="saving"
    :hide-header-close="saving"
    size="xl"
    @ok="onSubmit"
    @show="onShow"
    @hidden="onHidden"
    lazy
  >
    <div class="d-flex justify-content-center align-items-center" v-if="loading">
      <b-spinner class="mr-3" />
      <span>{{ $t('loading') }}</span>
    </div>

    <template v-else>
      <b-row>
        <b-col cols="10">
          <b-form-group
            :label="$t('clients.name')"
            label-for="name"
            :state="nameState"
            :invalid-feedback="nameError"
          >
            <b-form-input id="name" :autofocus="true" :state="nameState" v-model="name" />
          </b-form-group>
        </b-col>

        <b-col cols="2">
          <b-form-group :label="$t('clients.active')">
            <b-form-checkbox switch size="lg" v-model="isActive" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col cols="12">
          <b-btn :disabled="chosenConnections.length === serverConnections.length" @click="addConnection" v-if="canManageConnections">
            Add Connection
          </b-btn>
        </b-col>
      </b-row>

      <b-form-group :invalid-feedback="chosenConnections.length ? 'Connections require attention!' : 'At least 1 connection is required'" :state="chosenConnectionsState" v-if="canManageConnections">
        <b-row class="mb-2 align-items-center">
          <b-col cols="2">
            <strong :class="chosenConnectionsState === false ? 'text-danger' : ''">Connection</strong>
          </b-col>
          <b-col cols="3">
            <strong :class="chosenConnectionsState === false ? 'text-danger' : ''">Online Threshold</strong>
            <font-awesome-icon icon="question-circle" class="ml-1 text-muted" id="online-threshold-help" />
            <b-tooltip target="online-threshold-help" triggers="hover">
              {{ $t('clients.online_status_threshold_help') }}
            </b-tooltip>
          </b-col>
          <b-col cols="3">
            <strong :class="chosenConnectionsState === false ? 'text-danger' : ''">Screenshot Sync Interval</strong>
          </b-col>
          <b-col cols="3">
            <strong :class="chosenConnectionsState === false ? 'text-danger' : ''">Send Screenshot Capture Command</strong>
            <font-awesome-icon icon="question-circle" class="ml-1 text-muted" id="capture-screenshot-help" />
            <b-tooltip target="capture-screenshot-help" triggers="hover">
              Send a screenshot capture command to the translator after requesting a screenshot for a device.
            </b-tooltip>
          </b-col>
          <b-col cols="1">
            <!-- Blank heading for remove -->
          </b-col>
        </b-row>

        <div v-if="canManageConnections">
          <b-row v-for="(connection, index) in $v.chosenConnections.$each.$iter" :key="index" class="mb-2">
            <b-col cols="2">
              <b-select :options="connectionChoices" v-model="connection.id.$model" :state="calculateConnectionState(connection.id)" @change="updateRemovedConnections" />
              <b-form-invalid-feedback>
                Selection Required
              </b-form-invalid-feedback>
            </b-col>
            <b-col cols="3">
              <b-input-group>
                <b-input type="number" min="10"
                   v-model="connection.online_status_threshold.$model"
                   :state="calculateThresholdState(connection.online_status_threshold)"
                />
                <b-input-group-append>
                  <b-input-group-text>Minutes</b-input-group-text>
                </b-input-group-append>
                <b-form-invalid-feedback>
                  {{ calculateThresholdInvalidText(connection.online_status_threshold) }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-col>
            <b-col cols="3" v-if="connectionAllowsScreenshot(connection)">
              <b-input-group>
                <b-input type="number" min="10"
                         v-model="connection.screenshot_sync_interval.$model"
                         :state="calculateScreenshotState(connection.screenshot_sync_interval)"
                />
                <b-input-group-append>
                  <b-input-group-text>Minutes</b-input-group-text>
                </b-input-group-append>
                <b-form-invalid-feedback>
                  {{ calculateScreenshotInvalidText(connection.screenshot_sync_interval) }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-col>
            <b-col cols="3" v-else>
              <b-form-text>
                Screenshot functionality not available for the selected connection.
              </b-form-text>
            </b-col>
            <b-col cols="3" class="text-center" v-if="connectionAllowsScreenshot(connection)">
              <b-form-checkbox v-model="connection.send_screenshot_capture_command.$model"></b-form-checkbox>
            </b-col>
            <b-col cols="3" v-else>
              <b-form-text>
                Screenshot functionality not available for the selected connection.
              </b-form-text>
            </b-col>
            <b-col cols="1">
              <b-btn @click="removeConnection(index)" variant="transparent">
                <font-awesome-icon icon="times" />
              </b-btn>
            </b-col>
          </b-row>
        </div>
      </b-form-group>

      <b-row v-if="canManageConnections">
        <b-col cols="12" class="text-muted small">
          <font-awesome-icon icon="question-circle" class="ml-1 text-muted" />
          {{ $t('clients.online_status_threshold_help') }}
        </b-col>
      </b-row>
    </template>

    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="link" @click="cancel()">{{ $t('modal.cancel') }}</b-button>
      <b-button variant="primary" @click="ok()" :disabled="saving || loading">
        {{ $t('modal.save') }}
        <b-spinner small v-if="saving" />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import ErrorModal from '../../mixins/ErrorModal';
import pluralize from 'pluralize';
import { required, numeric, minValue, requiredIf } from 'vuelidate/lib/validators';

export default {
	props: {
		id: {
			default: 'add-client'
		},
		client: {
			default: null
		},
		edit: {
			type: Boolean,
			default: false
		},
		canManageConnections: {
			default: false,
			type: Boolean
		}
	},

	inject: [ 'connectionRepository', 'clientRepository' ],

	mixins: [ ErrorModal ],

	data () {
		return {
			name: '',
			serverConnections: [],
			chosenConnection: null,
			connections: [],
			threshold: process.env.VUE_APP_DEFAULT_ONLINE_STATUS_THRESHOLD || 10,
			thresholdUnit: process.env.VUE_APP_DEFAULT_ONLINE_STATUS_THRESHOLD_UNIT || 'minutes',
			isActive: true,
			submitted: false,
			saving: false,
			chosenConnections: [],
			removedConnections: [],
			loading: true
		};
	},

	validations () {
		return {
			name: {
				required
			},
			threshold: {
				required,
				numeric
			},
			chosenConnections: {
				required,
				$each: {
					id: {
						required
					},
					online_status_threshold: {
						required,
						minValue: minValue(10)
					},
					screenshot_sync_interval: {
						required: requiredIf(this.connectionAllowsScreenshot),
						minValue: minValue(10)
					},
					send_screenshot_capture_command: {}
				}
			}
		};
	},
	methods: {
		onShow () {
			if (!this.edit) {
				this.loadConnections();
				this.chosenConnections = [{
					id: null,
					online_status_threshold: 10,
					screenshot_sync_interval: 60,
					send_screenshot_capture_command: false
				}];
			}
		},

		onHidden () {
			this.$v.$reset();
			this.saving = false;
			this.submitted = false;
			this.connections = [];
			this.loading = true;
			this.$emit('hidden');
		},

		async onSubmit (bvEvent) {
			bvEvent.preventDefault();
			this.submitted = true;
			this.saving = true;

			this.$v.$touch();

			if (this.$v.$invalid) {
				this.saving = false;
			}
			else {
				let client;

				try {
					if (this.client === null) {
						client = await this.clientRepository.store(
							this.name,
							this.threshold,
							this.thresholdUnit,
							this.chosenConnections,
							this.isActive
						);

						this.$bvModal.hide(this.id);
						this.$emit('submit', client);
					}
					else {
						if (this.removedConnections.length > 0) {
							this.$bvModal.msgBoxConfirm('You are opting to remove connections linked to this client. This action will remove the devices linked to these connections from the client. Device monitors and monitor history will also be removed from these devices', {
								title: 'Are you sure?',
								okTitle: this.$t('modal.ok'),
								cancelTitle: this.$t('modal.cancel'),
								headerBgVariant: 'danger',
								headerTextVariant: 'light',
								okVariant: 'danger',
								cancelVariant: 'link'
							})
								.then(async data => {
									if (data) {
										client = await this.clientRepository.update(
											this.client.id,
											this.name,
											this.threshold,
											this.thresholdUnit,
											this.chosenConnections,
											this.isActive
										);

										this.$bvModal.hide(this.id);
										this.$emit('submit', client);
									}
								});
						}
						else {
							client = await this.clientRepository.update(
								this.client.id,
								this.name,
								this.threshold,
								this.thresholdUnit,
								this.chosenConnections,
								this.isActive
							);

							this.$bvModal.hide(this.id);
							this.$emit('submit', client);
						}
					}
				}
				catch (e) {
					this.showErrorModal(e, 'Error Saving Client');
				}

				// side bar refresh on client add/update
				// side bar may not have mounted yet, which would cause a data mismatch
				try {
					const payload = {};

					if (this.client) {
						payload.client = this.client.id;
					}

					await this.$store.dispatch('sidebar/refresh', payload);
				}
				catch (e) {
					// silently fail
				}

				this.saving = false;
				this.submitted = false;
			}
		},

		async loadConnections () {
			this.serverConnections = [];

			try {
				this.serverConnections = await this.connectionRepository.all(this.client);
				this.loading = false;
			}
			catch (e) {
				this.showErrorModal(e, 'Error Retrieving Connections');
			}
		},

		addConnection () {
			this.chosenConnections.push({
				id: null,
				online_status_threshold: 10,
				screenshot_sync_interval: 60,
				send_screenshot_capture_command: false
			});
		},

		removeConnection (index) {
			this.chosenConnections.splice(index, 1);
		},

		getConnection (id) {
			return this.serverConnections.find(connection => connection.id === id) || null;
		},
		calculateConnectionState (connectionValidator) {
			if (!connectionValidator.$dirty) {
				return null;
			}

			return !connectionValidator.$invalid;
		},
		calculateThresholdState (thresholdValidator) {
			if (!thresholdValidator.$dirty) {
				return null;
			}

			return !thresholdValidator.$invalid;
		},
		calculateThresholdInvalidText (thresholdValidator) {
			if (!thresholdValidator.required) {
				return 'Threshold is required';
			}

			if (!thresholdValidator.minValue) {
				return 'Threshold must be at least 10';
			}

			return 'Threshold is invalid';
		},
		updateRemovedConnections () {
			const currentConnectionIds = this.chosenConnections.map(element => element.id);

			this.removedConnections = this.connections.filter(element => !currentConnectionIds.includes(element.id));
		},
		calculateScreenshotState (thresholdValidator) {
			if (!thresholdValidator.$dirty) {
				return null;
			}

			return !thresholdValidator.$invalid;
		},
		calculateScreenshotInvalidText (thresholdValidator) {
			if (!thresholdValidator.required) {
				return 'Screenshot sync interval is required';
			}

			if (!thresholdValidator.minValue) {
				return 'Screenshot sync interval must be at least 10';
			}

			return 'Screenshot sync interval is invalid is invalid';
		},
		connectionAllowsScreenshot (connection) {
			const serverConnection = this.serverConnections.find(element => {
				let connectionId = connection.id;

				if (connection.id instanceof Object) {
					connectionId = connection.id.$model;
				}

				return element.id === connectionId;
			});

			if (serverConnection) {
				return serverConnection.allows_screenshots;
			}

			return false;
		},
		/**
     * determine if a connection has been previously selected
     *
     * @param {int} connectionId
     * @returns {boolean}
     */
		connectionIsSelected (connectionId) {
			for (const element of this.chosenConnections) {
				if (element.id === connectionId) {
					return true;
				}
			}

			return false;
		},
		/**
     * determines if the connection has been deleted
     *
     * @param connection
     * @returns {boolean}
     */
		connectionIsDeleted (connection) {
			return connection.deleted_at;
		}
	},

	computed: {
		modalTitle () {
			return this.client === null ? this.$t('clients.add_new') : this.$t('clients.edit');
		},

		connectionChoices () {
			return this.serverConnections.map(connection => {
				return {
					value: connection.id,
					text: connection.display_name,
					disabled: this.connectionIsSelected(connection.id) || this.connectionIsDeleted(connection)
				};
			});
		},

		thresholdUnits () {
			return [
				{
					value: 'minutes',
					text: pluralize(this.$t('clients.thresholdUnits.minutes'), this.threshold)
				}
			];
		},

		nameError () {
			if (this.submitted) {
				if (!this.$v.name.required) {
					return this.$t('validation.required');
				}
			}

			return '';
		},

		nameState () {
			return (this.nameError === '') ? null : false;
		},

		connectionsError () {
			if (this.submitted) {
				if (!this.$v.chosenConnections.required) {
					return this.$t('validation.required');
				}
			}

			return '';
		},

		connectionsState () {
			return (this.connectionsError === '') ? null : false;
		},

		thresholdError () {
			if (this.submitted) {
				if (!this.$v.threshold.required) {
					return this.$t('validation.required');
				}

				if (!this.$v.threshold.numeric) {
					return this.$t('validation.numeric');
				}
			}

			return '';
		},

		thresholdState () {
			return (this.thresholdError === '') ? null : false;
		},
		chosenConnectionsState () {
			if (!this.$v.chosenConnections.$dirty) {
				return null;
			}

			return !this.$v.chosenConnections.$invalid;
		}
	},

	watch: {
		client (client) {
			if (this.client !== null) {
				this.loadConnections();
				this.name = this.client.name;
				this.thresholdUnit = this.client.online_status_threshold_unit;
				this.isActive = this.client.is_active;
				this.connections = this.client.translator_connections.map(connection => {
					return {
						id: connection.id,
						online_status_threshold: connection.online_status_threshold,
						screenshot_sync_interval: connection.pivot.screenshot_sync_interval,
						send_screenshot_capture_command: connection.pivot.send_screenshot_capture_command === 1
					};
				});

				this.chosenConnections = this.client.translator_connections.map(connection => {
					return {
						id: connection.id,
						online_status_threshold: connection.online_status_threshold,
						screenshot_sync_interval: connection.pivot.screenshot_sync_interval,
						send_screenshot_capture_command: connection.pivot.send_screenshot_capture_command === 1
					};
				});
			}
		},
		chosenConnections () {
			this.updateRemovedConnections();
		}
	}
};
</script>

<style lang="scss">
@import '../../style/input-group'
</style>
